import React, {
  useCallback, useEffect, useMemo, useState,
} from "react";

import {
  CalendarOutlined,
  ClockCircleOutlined,
  DownOutlined,
  InboxOutlined,
  WarningFilled,
} from "@ant-design/icons";
import {
  Button, Form, Select,
} from "antd";
import moment from "moment";
import { toast } from "react-toastify";

import {
  renderSelectPopupScrollbar,
  selectPopupClassNames,
} from "../../../../Common/UIComponents/SelectPopupScrollbar";
import StyledInput from "../../../../Common/UIComponents/StyledInput";
import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import AddressDetails from "../../../Common/AddressDetails";
import {
  DEFAULT_DELIVERY_DETAIL,
  isAddressDetailValid,
  parseCustomerAddressFromResponse,
  parsePickupAddressList,
} from "../../Supply/DeliveryDetailDrawer";
import CreateStepLayout from "./CreateStepLayout";

import {
  DEFAULT_EARLIEST_POSSIBLE_TIME,
  DEFAULT_LATEST_POSSIBLE_TIME,
  HOURLY_TIME_OPTIONS,
  PICKUP_TYPE_KEYS,
  buildFedexAvailabilityPayload,
  extractAvailabilityOptions,
  filterLatestTimesByEarliest,
  getAvailabilityTimeState,
  getLocalCourierEarliestPickupDate,
  getPickupSummaryLabel,
  isFedexPickupType,
  isLocalCourierType,
  mapAvailabilityToDateOptions,
} from "../utils";

import { getCustomerAddress } from "../../../../../actions/sampleSubmission";
import { checkFedexPickupAvailability } from "../../../../../actions/supplyPickup";

import { ReactComponent as CalendarIcon } from "../../../../../assets/images/environment/calendarIcon.svg";

import "./PickupScheduleStep.css";

export default function PickupScheduleStep({
  pickupTypeLabel = "FedEx Pickup",
  pickupTypeKey = "fedex-pickup",
  initialSchedule = null,
  onBack,
  onContinue,
}) {
  const isFedexPickup = isFedexPickupType(pickupTypeKey);
  const isFedexDropoff = pickupTypeKey === PICKUP_TYPE_KEYS.FEDEX_DROPOFF;
  const isLocalCourier = isLocalCourierType(pickupTypeKey);
  const [form] = Form.useForm();
  const [addressLoading, setAddressLoading] = useState(true);
  const [pickupAddress, setPickupAddress] = useState(DEFAULT_DELIVERY_DETAIL);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [availabilityOptions, setAvailabilityOptions] = useState([]);
  const [pickupDateOptions, setPickupDateOptions] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [noPickupAvailability, setNoPickupAvailability] = useState(false);

  const watchedPickupDate = Form.useWatch("pickupDate", form);
  const watchedEarliest = Form.useWatch("earliestPossibleTime", form);
  const watchedLatest = Form.useWatch("latestPossibleTime", form);

  const selectedAvailabilityTimes = useMemo(
    () => (isFedexPickup
      ? getAvailabilityTimeState(availabilityOptions, watchedPickupDate)
      : {
        readyTimes: HOURLY_TIME_OPTIONS,
        latestTimes: HOURLY_TIME_OPTIONS,
        accessMinutes: 0,
      }),
    [availabilityOptions, isFedexPickup, watchedPickupDate],
  );

  const earliestTimeOptions = selectedAvailabilityTimes.readyTimes;
  const latestTimeOptions = filterLatestTimesByEarliest(
    selectedAvailabilityTimes.latestTimes,
    watchedEarliest,
    selectedAvailabilityTimes.accessMinutes,
  );

  const applyAvailabilityTimesForDate = useCallback((
    pickupDate,
    options,
    { preserveExisting = false, selectFirst = false } = {},
  ) => {
    const {
      readyTimes,
      latestTimes,
      defaultReady,
      accessMinutes,
    } = isFedexPickup
      ? getAvailabilityTimeState(options, pickupDate)
      : {
        readyTimes: HOURLY_TIME_OPTIONS,
        latestTimes: HOURLY_TIME_OPTIONS,
        defaultReady: DEFAULT_EARLIEST_POSSIBLE_TIME,
        accessMinutes: 0,
      };

    const currentEarliest = form.getFieldValue("earliestPossibleTime");
    const currentLatest = form.getFieldValue("latestPossibleTime");

    let earliest = defaultReady && readyTimes.includes(defaultReady)
      ? defaultReady
      : readyTimes[0];
    if (
      !selectFirst
      && preserveExisting
      && currentEarliest
      && readyTimes.includes(currentEarliest)
    ) {
      earliest = currentEarliest;
    }

    const filteredLatest = filterLatestTimesByEarliest(
      latestTimes,
      earliest,
      accessMinutes,
    );

    let latest = filteredLatest[0];
    if (!isFedexPickup && filteredLatest.includes(DEFAULT_LATEST_POSSIBLE_TIME)) {
      latest = DEFAULT_LATEST_POSSIBLE_TIME;
    }
    if (
      !selectFirst
      && preserveExisting
      && currentLatest
      && filteredLatest.includes(currentLatest)
    ) {
      latest = currentLatest;
    }

    form.setFieldsValue({
      earliestPossibleTime: earliest,
      latestPossibleTime: latest,
    });
  }, [form, isFedexPickup]);

  const fetchFedexPickupAvailability = useCallback(async (address) => {
    if (!isFedexPickup || !address?.address_1) return;

    setAvailabilityLoading(true);
    setNoPickupAvailability(false);
    const response = await checkFedexPickupAvailability(
      buildFedexAvailabilityPayload(address),
    );

    if (response.success) {
      const options = extractAvailabilityOptions(response);
      setAvailabilityOptions(options);
      const dateOptions = mapAvailabilityToDateOptions(options);
      setPickupDateOptions(dateOptions);

      if (dateOptions.length === 0) {
        setNoPickupAvailability(true);
        form.setFieldsValue({
          pickupDate: undefined,
          earliestPossibleTime: undefined,
          latestPossibleTime: undefined,
        });
      } else {
        setNoPickupAvailability(false);
        const currentPickupDate = form.getFieldValue("pickupDate");
        const currentKey = currentPickupDate
          ? moment(currentPickupDate, ["YYYY-MM-DD", "MM/DD/YYYY", "MM/DD/YY"], true).format("YYYY-MM-DD")
          : "";
        const hasValidCurrentDate = currentKey
          && dateOptions.some((opt) => opt.value === currentKey);
        const selectedDate = hasValidCurrentDate
          ? currentKey
          : dateOptions[0].value;

        form.setFieldsValue({ pickupDate: selectedDate });
        applyAvailabilityTimesForDate(selectedDate, options, {
          preserveExisting: hasValidCurrentDate,
          selectFirst: !hasValidCurrentDate,
        });
      }
    } else if (!response.canceled) {
      setAvailabilityOptions([]);
      setPickupDateOptions([]);
      setNoPickupAvailability(true);
      form.setFieldsValue({
        pickupDate: undefined,
        earliestPossibleTime: undefined,
        latestPossibleTime: undefined,
      });
      toast.error(response.message || "Unable to check FedEx pickup availability.");
    }
    setAvailabilityLoading(false);
  }, [applyAvailabilityTimesForDate, form, isFedexPickup]);

  useEffect(() => {
    let cancelled = false;
    const loadAddress = async () => {
      setAddressLoading(true);
      const { success, message, address } = await getCustomerAddress(null, "");
      if (cancelled) return;
      if (success) {
        const pickupAddressList = parsePickupAddressList(address?.pickup_address);
        const parsed = pickupAddressList[0] || parseCustomerAddressFromResponse(address);
        const savedList = pickupAddressList.length > 0 ? pickupAddressList : [parsed];
        const savedFromSchedule = initialSchedule?.pickupAddress;
        const restored = savedFromSchedule && isAddressDetailValid(savedFromSchedule)
          ? savedFromSchedule
          : parsed;
        setPickupAddress(restored);
        setSavedAddresses(savedList);
        setAddressLoading(false);
        if (isFedexPickup) {
          await fetchFedexPickupAvailability(restored);
        }
        return;
      }
      toast.error(message || "Failed to load pickup address");
      setAddressLoading(false);
    };
    loadAddress();
    return () => { cancelled = true; };
    // Intentionally run once on mount; address edits call fetchFedexPickupAvailability directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // FedEx Drop-off has no pickup window — the schedule card is not shown.
    if (isFedexDropoff) return;

    // Local courier: after 7:00 AM default to tomorrow (same-day cut-off).
    // FedEx Pickup: leave empty until availability loads.
    let defaultPickupDate = moment().format("YYYY-MM-DD");
    if (isLocalCourier) {
      defaultPickupDate = getLocalCourierEarliestPickupDate();
    }
    const resolvedPickupDate = isFedexPickup
      ? (initialSchedule?.pickupDate || undefined)
      : (initialSchedule?.pickupDate || defaultPickupDate);

    const initialValues = {
      pickupDate: resolvedPickupDate,
      earliestPossibleTime: initialSchedule?.earliestPossibleTime || undefined,
      latestPossibleTime: initialSchedule?.latestPossibleTime || undefined,
    };
    if (isFedexPickup) {
      initialValues.fedexService = initialSchedule?.fedexService || "FedEx Overnight";
    }
    form.setFieldsValue(initialValues);

    if (
      !isFedexPickup
      && resolvedPickupDate
      && !initialSchedule?.earliestPossibleTime
    ) {
      applyAvailabilityTimesForDate(resolvedPickupDate, [], { selectFirst: true });
    }
  }, [
    applyAvailabilityTimesForDate,
    form,
    initialSchedule,
    isFedexDropoff,
    isFedexPickup,
    isLocalCourier,
  ]);

  const summaryLabel = useMemo(
    () => getPickupSummaryLabel(watchedPickupDate, watchedEarliest, watchedLatest),
    [watchedPickupDate, watchedEarliest, watchedLatest],
  );

  const canContinue = isFedexDropoff
    ? !addressLoading
    : Boolean(watchedPickupDate && watchedEarliest && watchedLatest);

  const handleUpdateAddress = (values) => {
    const nextAddress = { ...DEFAULT_DELIVERY_DETAIL, ...values };
    setPickupAddress(nextAddress);
    if (isFedexPickup) {
      fetchFedexPickupAvailability(nextAddress);
    }
  };

  const handlePickupDateChange = (value) => {
    if (!value) {
      form.setFieldsValue({
        pickupDate: undefined,
        earliestPossibleTime: undefined,
        latestPossibleTime: undefined,
      });
      return;
    }
    form.setFieldsValue({ pickupDate: value });
    const hasExistingTimes = Boolean(form.getFieldValue("earliestPossibleTime"));
    applyAvailabilityTimesForDate(value, availabilityOptions, {
      // Local courier / FedEx Drop-off: keep 8 AM–5 PM (or user picks) when already set.
      preserveExisting: !isFedexPickup && hasExistingTimes,
      selectFirst: isFedexPickup || !hasExistingTimes,
    });
  };

  const handleNonFedexDateChange = (date) => {
    if (!date) {
      handlePickupDateChange(undefined);
      return;
    }
    const dateValue = moment(date).format("YYYY-MM-DD");
    handlePickupDateChange(dateValue);
  };

  const handleEarliestTimeChange = (value) => {
    const {
      latestTimes,
      accessMinutes,
    } = isFedexPickup
      ? getAvailabilityTimeState(availabilityOptions, form.getFieldValue("pickupDate"))
      : {
        latestTimes: HOURLY_TIME_OPTIONS,
        accessMinutes: 0,
      };
    const filteredLatest = filterLatestTimesByEarliest(latestTimes, value, accessMinutes);
    form.setFieldsValue({
      earliestPossibleTime: value,
      latestPossibleTime: filteredLatest[0],
    });
  };

  const handleContinue = async () => {
    if (isFedexDropoff) {
      if (!isAddressDetailValid(pickupAddress)) {
        toast.error("Pickup address is not valid, please contact Genista Biosciences");
        return;
      }
      // Labels still need a ship date. Drop-off has no customer-selected window.
      onContinue({
        pickupDate: initialSchedule?.pickupDate || moment().format("YYYY-MM-DD"),
        earliestPossibleTime: "",
        latestPossibleTime: "",
        pickupAddress,
      });
      return;
    }

    try {
      const values = await form.validateFields();
      if (!isAddressDetailValid(pickupAddress)) {
        toast.error("Pickup address is not valid, please contact Genista Biosciences");
        return;
      }
      const schedule = {
        pickupDate: values.pickupDate || "",
        earliestPossibleTime: values.earliestPossibleTime,
        latestPossibleTime: values.latestPossibleTime,
        pickupAddress,
      };
      if (isFedexPickup) {
        schedule.fedexService = values.fedexService || "FedEx Overnight";
      }
      onContinue(schedule);
    } catch (e) {
      // validation errors shown by form
    }
  };

  const footer = (
    <div className="PickupScheduleStep__Footer">
      <Button
        type="default"
        className="PickupScheduleStep__GoBackBtn"
        onClick={onBack}
      >
        Go Back
      </Button>
      <div className="PickupScheduleStep__FooterRight">
        {summaryLabel && (
          <span className="PickupScheduleStep__Summary">{summaryLabel}</span>
        )}
        <Button
          type="primary"
          className="PickupScheduleStep__ContinueBtn"
          disabled={!canContinue}
          onClick={handleContinue}
        >
          Continue
        </Button>
      </div>
    </div>
  );

  const renderPickupDateField = () => {
    if (isFedexPickup) {
      return (
        <Form.Item
          label="Select Pickup Date"
          name="pickupDate"
          className="PickupScheduleStep__Field"
          rules={[{ required: true, message: "Please select pickup date" }]}
        >
          <Select
            className="PickupScheduleStep__Select"
            placeholder={availabilityLoading ? "Checking availability..." : "Select Pickup Date"}
            allowClear
            loading={availabilityLoading}
            prefix={<CalendarOutlined className="PickupScheduleStep__FieldIcon" />}
            suffixIcon={<DownOutlined className="PickupScheduleStep__SelectCaretIcon" />}
            popupMatchSelectWidth
            disabled={!availabilityLoading && pickupDateOptions.length === 0}
            open={availabilityLoading ? false : undefined}
            onChange={handlePickupDateChange}
            options={pickupDateOptions}
            classNames={selectPopupClassNames()}
            virtual={false}
            listHeight={256}
            popupRender={(menu) => renderSelectPopupScrollbar(menu, {
              optionCount: (pickupDateOptions || []).length,
            })}
          />
        </Form.Item>
      );
    }

    return (
      <Form.Item
        label="Select Pickup Date"
        name="pickupDate"
        className="PickupScheduleStep__Field"
        key={watchedPickupDate || "empty-pickup-date"}
        rules={[
          { required: true, message: "Please select pickup date" },
          ...(isFedexDropoff
            ? [{
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const selected = moment(value, ["YYYY-MM-DD", "MM/DD/YYYY"], true).startOf("day");
                const maxAllowed = moment().startOf("day").add(9, "days");
                if (selected.isValid() && selected.isAfter(maxAllowed)) {
                  return Promise.reject(
                    new Error("Pickup date cannot be more than 10 days from today"),
                  );
                }
                return Promise.resolve();
              },
            }]
            : []),
        ]}
        getValueProps={(value) => ({
          value: value
            ? moment(value, ["YYYY-MM-DD", "MM/DD/YYYY"], true).format("MM/DD/YYYY")
            : "",
        })}
        normalize={(value) => {
          if (!value) return undefined;
          const parsed = moment(value, ["YYYY-MM-DD", "MM/DD/YYYY"], true);
          return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value;
        }}
      >
        <StyledInput
          allowClear={false}
          type="datepicker"
          placeholder="Select Pickup Date"
          className="PickupScheduleStep__DatePickerInput"
          datePickerData={{
            resetDateValue: (val) => {
              if (!val) {
                handlePickupDateChange(undefined);
                return;
              }
              const parsed = moment(val, ["MM/DD/YYYY", "YYYY-MM-DD"], true);
              handlePickupDateChange(parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined);
            },
            displayDateFormat: "MM/DD/YYYY",
            datePickerProps: {
              searchOnClear: true,
              mode: "single",
              dateFormat: "m/d/Y",
              minDate: isLocalCourier
                ? getLocalCourierEarliestPickupDate("MM/DD/YYYY")
                : moment().format("MM/DD/YYYY"),
              maxDate: isFedexDropoff
                ? moment().add(9, "days").format("MM/DD/YYYY")
                : moment().add(10, "years").format("MM/DD/YYYY"),
              handleSearchDate: handleNonFedexDateChange,
              getReportDates: () => [],
              popoverPlacement: "bottomLeft",
              datePickerDisplayProps: {
                displayClassName: "PickupScheduleStep__DatePickerDisplay",
                customCalendarIcon: <CalendarIcon width={20} height={20} />,
                displayTextPosition: "left",
                showText: false,
              },
            },
          }}
        />
      </Form.Item>
    );
  };

  return (
    <CreateStepLayout
      pickupTypeLabel={pickupTypeLabel}
      pickupTypeKey={pickupTypeKey}
      activeStepKey="pickup-schedule"
      onBack={onBack}
    >
      <div className="PickupScheduleStep">
        <div className="PickupScheduleStep__ScrollArea">
          <StyledScrollbar>
            <div className="PickupScheduleStep__Inner">
              <div className={`PickupScheduleStep__Grid${isFedexDropoff ? " PickupScheduleStep__Grid--single" : ""}`}>
                {!isFedexDropoff && (
                <div className="PickupScheduleStep__Card">
                  <h2 className="PickupScheduleStep__CardTitle">Pickup Schedule</h2>
                  <Form
                    form={form}
                    layout="vertical"
                    className="PickupScheduleStep__Form"
                    requiredMark={false}
                  >
                    {isFedexPickup && (
                      <Form.Item
                        label="FedEx Service"
                        className="PickupScheduleStep__Field"
                      >
                        <div className="PickupScheduleStep__ServiceDisplay">
                          <InboxOutlined className="PickupScheduleStep__FieldIcon" aria-hidden />
                          <span>FedEx Overnight</span>
                        </div>
                      </Form.Item>
                    )}

                    {renderPickupDateField()}

                    <div className="PickupScheduleStep__TimeRow">
                      <Form.Item
                        label="Earliest Possible Time"
                        name="earliestPossibleTime"
                        className="PickupScheduleStep__Field"
                        rules={[{ required: true, message: "Please select earliest time" }]}
                      >
                        <Select
                          className="PickupScheduleStep__Select"
                          placeholder={
                            isFedexPickup && availabilityLoading
                              ? "Checking availability..."
                              : "Select Time"
                          }
                          allowClear
                          loading={isFedexPickup && availabilityLoading}
                          prefix={<ClockCircleOutlined className="PickupScheduleStep__FieldIcon" />}
                          suffixIcon={<DownOutlined className="PickupScheduleStep__SelectCaretIcon" />}
                          popupMatchSelectWidth
                          disabled={
                            (isFedexPickup && availabilityLoading)
                            || !watchedPickupDate
                            || earliestTimeOptions.length === 0
                          }
                          open={isFedexPickup && availabilityLoading ? false : undefined}
                          onChange={handleEarliestTimeChange}
                          options={earliestTimeOptions.map((time) => ({
                            value: time,
                            label: time,
                          }))}
                          classNames={selectPopupClassNames()}
                          virtual={false}
                          listHeight={256}
                          popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                            optionCount: (earliestTimeOptions || []).length,
                          })}
                        />
                      </Form.Item>

                      <Form.Item
                        label="Latest Possible Time"
                        name="latestPossibleTime"
                        className="PickupScheduleStep__Field"
                        rules={[{ required: true, message: "Please select latest time" }]}
                      >
                        <Select
                          className="PickupScheduleStep__Select"
                          placeholder={
                            isFedexPickup && availabilityLoading
                              ? "Checking availability..."
                              : "Select Time"
                          }
                          allowClear
                          loading={isFedexPickup && availabilityLoading}
                          prefix={<ClockCircleOutlined className="PickupScheduleStep__FieldIcon" />}
                          suffixIcon={<DownOutlined className="PickupScheduleStep__SelectCaretIcon" />}
                          popupMatchSelectWidth
                          disabled={
                            (isFedexPickup && availabilityLoading)
                            || !watchedPickupDate
                            || latestTimeOptions.length === 0
                          }
                          open={isFedexPickup && availabilityLoading ? false : undefined}
                          options={latestTimeOptions.map((time) => ({
                            value: time,
                            label: time,
                          }))}
                          classNames={selectPopupClassNames()}
                          virtual={false}
                          listHeight={256}
                          popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                            optionCount: (latestTimeOptions || []).length,
                          })}
                        />
                      </Form.Item>
                    </div>

                    {isFedexPickup && noPickupAvailability && !availabilityLoading && (
                      <div
                        className="PickupScheduleStep__AvailabilityError"
                        role="alert"
                      >
                        <WarningFilled
                          className="PickupScheduleStep__AvailabilityErrorIcon"
                          aria-hidden
                        />
                        <span className="PickupScheduleStep__AvailabilityErrorText">
                          No time slots available for the current address. Either select a
                          different address or choose FedEx Drop-off.
                        </span>
                      </div>
                    )}
                  </Form>
                </div>
                )}

                <AddressDetails
                  className="PickupScheduleStep__Card PickupScheduleStep__AddressCard"
                  title={isFedexDropoff ? "Address Details" : "Pickup Address Details"}
                  selectId="pickup-default-address"
                  addresses={savedAddresses}
                  selectedAddress={pickupAddress}
                  loading={addressLoading || (isFedexPickup && availabilityLoading)}
                  onSave={handleUpdateAddress}
                />
              </div>
            </div>
          </StyledScrollbar>
        </div>

        {footer}
      </div>
    </CreateStepLayout>
  );
}
